import React from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { createSelector } from 'reselect';

import ProjectCountPortal from './project_count_portal';
import LeadsList from 'components/project/leads_list';
import ContactRolesList from 'components/project/contact_roles_list';
import ContactAddProjectLeadButton from 'bundles/contact/projects/contact_add_project_lead_button';
import ContactAddProjectContactRoleButton from 'bundles/contact/projects/contact_add_project_contact_role_button';
import EmptyState from 'shared/empty_state';

const getContactRoles = createSelector(
  [props => props.contactRoles],
  contactRoles => sortBy(Object.values(contactRoles), contactRole => contactRole.parentName.toLowerCase())
);

const getLeads = createSelector(
  [props => props.leads],
  leads => sortBy(Object.values(leads), lead => lead.projectName.toLowerCase())
);

export default class ContactProjectSection extends React.Component {
  static propTypes = {
    contact: PropTypes.object.isRequired,
    contactRoles: PropTypes.object.isRequired,
    leads: PropTypes.object.isRequired,
    leadStatuses: PropTypes.array.isRequired,
    referralSources: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })),
    relationships: PropTypes.array.isRequired,
    showLeadsSection: PropTypes.bool.isRequired
  };

  state = {
    contactRoles: this.props.contactRoles,
    leads: this.props.leads
  }

  hasContactRoles = () => getContactRoles(this.state).length > 0

  handleContactRoleChange = (newRole) => {
    this.setState((prevState) => {
      return { contactRoles: { ...prevState.contactRoles, [newRole.id]: newRole } };
    });
  }

  hasLeads = () => getLeads(this.state).length > 0

  handleLeadChange = (newLead) => {
    this.setState((prevState) => {
      return { leads: { ...prevState.leads, [newLead.id]: newLead } };
    });
  }

  handleDeleteContactRole = (role) => {
    this.setState(({ contactRoles }) => {
      const { [role.id]: _,  ...remainingRoles } = contactRoles;

      return { contactRoles: remainingRoles };
    });
  };

  renderLeadsSection() {
    if (!this.props.showLeadsSection) return;

    return (
      <div>
        {this.renderLeadsHeader()}
        {this.hasLeads() ? this.renderLeadsList() : this.renderEmptyState()}
      </div>);
  }

  render() {
    return (
      <div className="d-flex flex-column gap-4">
        {this.renderLeadsSection()}
        <div>
          {this.renderContactRolesHeader()}
          {this.hasContactRoles() ? this.renderContactRolesList() : this.renderEmptyState()}
        </div>
        <ProjectCountPortal count={[...getContactRoles(this.state), ...getLeads(this.state)].length} />
      </div>
    );
  }

  renderContactRolesHeader() {
    const { contact, relationships } = this.props;

    return (
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fs-large">
          Contact
        </div>

        <ContactAddProjectContactRoleButton
          className="btn btn-primary"
          contact={contact}
          relationships={relationships}
          onAdd={this.handleContactRoleChange}
        />
      </div>
    );
  }

  renderLeadsHeader() {
    const { contact, leadStatuses, referralSources } = this.props;

    return (
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fs-large">
          Leads
        </div>

        <ContactAddProjectLeadButton
          className="btn btn-primary"
          contact={contact}
          leadStatuses={leadStatuses}
          referralSources={referralSources}
          onAdd={this.handleLeadChange}
        />
      </div>
    );
  }

  renderEmptyState() {
    return (
      <EmptyState bodyText="No projects are associated with this contact." />
    );
  }

  renderContactRolesList() {
    return <ContactRolesList contactRoles={getContactRoles(this.state)} onDelete={this.handleDeleteContactRole} />;
  }

  renderLeadsList() {
    return <LeadsList leads={getLeads(this.state)} />;
  }
}
